import _ from 'lodash';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';

import { ClickAwayListener } from '@mui/material';
import { withStyles } from '@mui/styles';

import SelectClasses, {
  MultiSelectStyles,
  MultiSelectStyles1,
} from './MultiSelectStyles';

import MultiSelectButtonWithStyles from './MultiSelectButton';
import {
  Dropdown,
  DropdownIndicator,
  MenuList,
  Option,
} from './MultiSelectComponentOverrides';
import YellowWarningIcon from '../../../_assets/svg/YellowWarning.svg';
import SimpleTooltip from '../../SimpleTooltip';

class MultiSelect extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      values: props.values,
    };
    this.onSelectChange = this.onSelectChange.bind(this);
    this.toggleOpen = this.toggleOpen.bind(this);
    this.handleClickAway = this.handleClickAway.bind(this);
    this.toggleAllHandler = this.toggleAllHandler.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { props } = this;

    if (!_.isEmpty(prevProps.values) && _.isEmpty(props.values)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        values: [],
      });
    }

    if (_.isEmpty(prevProps.values) && !_.isEmpty(props.values)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        values: props.values,
      });
    }

    if (!_.isEqual(prevProps.values, props.values)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        values: props.values,
      });
    }
  }

  render() {
    const { props, state } = this;

    if (!props.isShowing) {
      return null;
    }

    let _Option = Option;
    if (_.get(props, 'components.Option')) {
      _Option = props.components.Option;
    }

    const isRequired = props.required ? { required: true } : {};

    return (
      <Fragment>
        {props.label && (
          <label
            className={`${props.classes.label} ${props.labelClass}`}
            htmlFor={props.name}
          >
            {props.label}
            {props.required ? (
              <i
                style={{
                  color: '#f95f53',
                  fontSize: '16px',
                  lineHeight: '16px',
                }}
              >
                *
              </i>
            ) : (
              ''
            )}
          </label>
        )}
        <Dropdown
          id={props.name}
          isOpen={state.isOpen}
          onClose={this.toggleOpen}
          disabled={props.disabled}
          target={
            props.components.DropDownButton ? (
              React.createElement(props.components.DropDownButton, {
                fullWidth: true,
                toggleOpen: this.toggleOpen,
                disabled: props.disabled,
                buttonPlaceholder: props.buttonPlaceholder,
                data: state,
                ...props,
              })
            ) : (
              <MultiSelectButtonWithStyles
                isOpen={state.isOpen}
                fullWidth
                toggleOpen={this.toggleOpen}
                disabled={props.disabled}
                classname={props.classes.select}
                error={props.error}
                customStyle={props.customStyle}
              >
                <span>{generateSelectedLabel(state, props)}</span>
                <ArrowDropDown className={props.classes.buttonDown} />
              </MultiSelectButtonWithStyles>
            )
          }
        >
          <ClickAwayListener onClickAway={this.handleClickAway}>
            {this.state.isOpen && (
              <Select
                autoFocus
                backspaceRemovesValue={false}
                components={{
                  DropdownIndicator,
                  Option: _Option,
                  MenuList,
                  IndicatorSeparator: null,
                }}
                toggleAllHandler={this.toggleAllHandler}
                classes={props.classes}
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                isClearable={false}
                isMulti={props.isMulti}
                supportSelectAll={props.supportSelectAll}
                menuIsOpen
                onChange={this.onSelectChange}
                options={props.options}
                placeholder={props.placeholder}
                styles={
                  !props.supportSelectAll
                    ? MultiSelectStyles(props.theme)
                    : MultiSelectStyles1(props.theme)
                }
                tabSelectsValue={false}
                value={state.values}
                {...isRequired}
                onFocus={props.onFocusHandler}
                onBlur={props.onBlurHandler}
                onMenuScrollToBottom={props.onMenuScrollToBottom}
                onMenuScrollToTop={props.onMenuScrollToTop}
                inputProps={
                  props.inputProps ? { required: true } : { required: false }
                }
                onInputChange={props.inputChange}
                isLoading={props.isLoading}
                noOptionsMessage={props.noOptionsMessage}
              />
            )}
          </ClickAwayListener>
        </Dropdown>
        {props.error && (
          <span className={props.classes.errorText}>{props.error}</span>
        )}
        {!props.error && props.showErrorPlace && (
          <span className={props.classes.noErrorText} aria-hidden>
            error ph
          </span>
        )}
      </Fragment>
    );
  }

  toggleAllHandler() {
    this.props.toggleAllHandler();
  }

  toggleOpen() {
    if (!this.props.disabled) {
      this.setState((prevState) => ({
        isOpen: !prevState.isOpen,
      }));
    }
  }

  handleClickAway() {
    this.setState({
      isOpen: false,
    });
  }

  onSelectChange(values) {
    const { props } = this;
    this.setState({ values });

    if (typeof props.onChange === 'function') {
      props.onChange(props.name, values);
    }

    // close menu on select if not a multi select
    if (!props.isMulti) {
      this.toggleOpen();
    }
  }
}

function generateSelectedLabel(
  state,
  { customStyle, buttonPlaceholder, customLabel }
) {
  if (customLabel) {
    return customLabel;
  }

  if (customStyle && Array.isArray(state.values)) {
    const str = state.values.map(({ label }) => label).join(', ');

    if (state.values.length === 1) {
      return str;
    }

    if (state.values.length > 1) {
      return (
        <SimpleTooltip title={str}>
          <YellowWarningIcon />
        </SimpleTooltip>
      );
    }
    return buttonPlaceholder;
  }

  if (Array.isArray(state.values)) {
    if (state.values.length > 0) {
      return `${state.values
        .map(({ label }, index) => (index < 2 ? label : ''))
        .slice(0, 2)
        .join(', ')}${state.values.length > 2 ? ', ...' : ''}`;
    }
  }

  if (typeof state.values === 'object' && !_.isEmpty(state.values)) {
    return state.values.label;
  }

  return buttonPlaceholder;
}

MultiSelect.defaultProps = {
  isMulti: true,
  isShowing: true,
  disabled: false,
  buttonPlaceholder: 'Select',
  options: [],
  values: [],
  components: {},
  showErrorPlace: true,
};

MultiSelect.propTypes = {
  components: PropTypes.object,
  buttonPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isShowing: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  showErrorPlace: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  values: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
};

export default withStyles(SelectClasses, { withTheme: true })(MultiSelect);
